import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { DialogFooter } from "@/components/ui/dialog";
import React from "react";

const formSchema = z.object({
  tagName: z.string().min(1),
});

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText?: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
  defaultValues?: FormSchemaType;
  onCancel?: () => void;
  showAsDialog?: boolean;
};

export const TagMetaForm: React.FC<Props> = ({
  buttonText,
  onSubmit,
  isLoading,
  defaultValues,
  onCancel,
  showAsDialog = false,
}) => {
  const { t } = useTranslation();
  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      tagName: "",
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    if (showAsDialog) {
      e.preventDefault();
      e.stopPropagation();
    }
    form.handleSubmit(onSubmit)(e);
  };

  const ButtonContainer = showAsDialog ? DialogFooter : "div";

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-8">
        <FormField
          control={form.control}
          name="tagName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("common.name")}</FormLabel>
              <FormControl>
                <Input placeholder={t("common.name")} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <ButtonContainer className={showAsDialog ? "" : "flex justify-end"}>
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              {t("common.cancel")}
            </Button>
          )}
          <Button type="submit" disabled={isLoading}>
            {buttonText || t("common.add")}
            {isLoading && <LoadingSmall />}
          </Button>
        </ButtonContainer>
      </form>
    </Form>
  );
};
